import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';

const { Text } = Typography;

/**
 * AI-revise an UNPUBLISHED template body per the curator's instruction
 * (POST /admin/library-templates/:id/ai-revise). The API replaces the body, stamps the new
 * AI provenance (no agreement score is computed for library drafts), clears any prior review stamp and drops an `in_review`
 * row back to `draft` — the human must review the text that will actually be published.
 * approved / published / retired rows are immutable (409): fork a new version first.
 * platform_admin only (server-enforced).
 */
const LibraryAiReviseModal = ({ open, item, onClose, onRevised }) => {
    const { request } = useApiRequest();
    const [instruction, setInstruction] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setInstruction('');
            setError(null);
        }
    }, [open]);

    const handleOk = async () => {
        if (!instruction.trim()) return setError('Tell the AI what to change.');
        setBusy(true);
        setError(null);
        try {
            const { data } = await request('POST', `/admin/library-templates/${item.id}/ai-revise`, {
                body: { instruction: instruction.trim() },
            });
            message.success('Revision saved as a new AI draft - review it before submitting.');
            onRevised(data.item);
        } catch (err) {
            setError(err.message || 'The AI revision failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title={item ? `AI-revise ${item.controlRef} v${item.version}` : 'AI-revise'}
            okText="Revise with AI"
            confirmLoading={busy}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Alert
                    type="warning"
                    showIcon
                    message="This replaces the current body"
                    description={
                        item?.status === 'in_review'
                            ? 'The template returns to Draft and its review is cleared, because the text being reviewed has changed.'
                            : 'The new text is an AI draft and must be reviewed before it can be submitted.'
                    }
                />
                <div>
                    <Text strong>Instruction</Text>
                    <Input.TextArea
                        aria-label="Revision instruction"
                        rows={5}
                        maxLength={2000}
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                        placeholder="e.g. Add a section on contractor access and shorten the scope statement."
                    />
                </div>
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

export default LibraryAiReviseModal;
